/**
 * Перехват падения раздела.
 *
 * React устроен так, что ошибка при отрисовке сносит ВЕСЬ корень: экран
 * становится белым, и по нему не понять ни что случилось, ни что делать. На
 * ночной проверке 18.09.2026 так и вышло: запись без одного поля — и раздел
 * «Документы» превращался в пустое место, хотя всё остальное в программе
 * работало.
 *
 * Здесь падение останавливается на границе раздела: меню, шапка и соседние
 * разделы продолжают работать, а на месте упавшего показывается, что
 * произошло, и две кнопки — попробовать снова и перезагрузить программу.
 *
 * Текст ошибки показан НАМЕРЕННО. Владелец работает один и сам передаёт
 * сообщения мне; «что-то пошло не так» без подробностей означает, что
 * причину придётся угадывать по описанию.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';

interface Props {
  /** Чтобы при переходе в другой раздел ошибка не «прилипала». */
  ключ: string;
  children: ReactNode;
}

interface State {
  ошибка: Error | null;
}

export class ScreenBoundary extends Component<Props, State> {
  override state: State = { ошибка: null };

  static getDerivedStateFromError(ошибка: Error): State {
    return { ошибка };
  }

  override componentDidUpdate(prev: Props): void {
    // Сменили раздел — начинаем с чистого листа.
    if (prev.ключ !== this.props.ключ && this.state.ошибка) this.setState({ ошибка: null });
  }

  override componentDidCatch(ошибка: Error, сведения: ErrorInfo): void {
    // В журнал браузера — со стеком разметки: по нему видно, какой кусок упал.
    console.error('Раздел упал:', ошибка, сведения.componentStack);
  }

  override render(): ReactNode {
    const { ошибка } = this.state;
    if (!ошибка) return this.props.children;

    return (
      <div
        className="mx-auto mt-6 max-w-2xl rounded-xl border p-5"
        style={{ borderColor: 'var(--otc-coral)', background: 'var(--otc-surface)' }}
      >
        <p className="flex items-center gap-2 font-display text-lg font-semibold" style={{ color: 'var(--otc-coral)' }}>
          <AlertTriangle size={20} /> Раздел не открылся
        </p>

        <p className="mt-2 text-sm text-ink">
          Остальная программа работает — можно перейти в другой раздел. Данные не пострадали:
          на экране ничего не сохранялось.
        </p>

        <p
          className="tabular mt-3 overflow-x-auto rounded-lg border px-3 py-2 text-xs text-ink2"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
        >
          {ошибка.message || String(ошибка)}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => this.setState({ ошибка: null })}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-transparent px-3.5 py-2 text-sm font-medium text-white"
            style={{ background: 'var(--otc-accent)' }}
          >
            <RefreshCw size={15} /> Попробовать снова
          </button>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-3.5 py-2 text-sm font-medium text-ink"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            <RotateCcw size={15} /> Перезагрузить программу
          </button>
        </div>

        <p className="mt-3 text-xs text-ink2">
          Если повторяется — пришлите снимок этого окна вместе с тем, что делали перед ним.
        </p>
      </div>
    );
  }
}
